import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

type ConfirmationModalProps = {
  isOpen: boolean;
  title: string;
  description?: ReactNode;
  confirmText: string;
  cancelText?: string;
  tone?: 'danger' | 'default';
  isConfirming?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  icon?: ReactNode;
};

const ConfirmationModal = ({
  isOpen,
  title,
  description,
  confirmText,
  cancelText = 'Cancel',
  tone = 'default',
  isConfirming = false,
  onConfirm,
  onCancel,
  icon,
}: ConfirmationModalProps) => {
  if (!isOpen) return null;

  const isDanger = tone === 'danger';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <div
            className={
              isDanger
                ? 'mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100'
                : 'mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-slate-100'
            }
          >
            {icon ?? <AlertCircle className={isDanger ? 'h-6 w-6 text-red-600' : 'h-6 w-6 text-slate-700'} />}
          </div>

          <h3 className="text-lg font-medium text-gray-900 mt-4">{title}</h3>

          {description && <div className="mt-2 px-7 py-3 text-sm text-gray-600">{description}</div>}

          <div className="flex justify-center space-x-3 px-4 py-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800"
              disabled={isConfirming}
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isConfirming}
              className={
                isDanger
                  ? 'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-60 disabled:cursor-not-allowed'
                  : 'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800 disabled:opacity-60 disabled:cursor-not-allowed'
              }
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;

